/**
 * URL Validation Utility
 * 
 * Security utility for validating URLs before navigation.
 * Prevents XSS attacks through malicious URLs like javascript: protocol.
 * 
 * @module utils/urlValidator
 */

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Allowed protocols for navigation
 * Only HTTPS is allowed in production for security
 */
const ALLOWED_PROTOCOLS = ['https:', 'http:'] as const;

/**
 * Allowed domains for app URLs
 * Add domains here that are trusted for navigation
 */
const ALLOWED_DOMAINS = [
    // HAAT domains
    'haat.delivery',

    // Internal/LAN domains
    'lan-wan.net',

    // Third-party services
    'monday.com',
    'manychat.com',
    'make.com',
    'intercom.com',
    'voicenter.co.il',
    'cloudxt.net',

    // Development
    'localhost',
    '127.0.0.1',
] as const;

/**
 * Dangerous protocols that should always be blocked
 */
const DANGEROUS_PROTOCOLS = [
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
] as const;

// =============================================================================
// VALIDATION FUNCTIONS
// =============================================================================

/**
 * Checks if a URL has a dangerous protocol
 * 
 * @param url - URL to check
 * @returns true if URL has dangerous protocol
 */
const hasDangerousProtocol = (url: string): boolean => {
    const lowerUrl = url.toLowerCase().trim();
    return DANGEROUS_PROTOCOLS.some(protocol =>
        lowerUrl.startsWith(protocol)
    );
};

/**
 * Validates a URL for safe navigation
 * 
 * Security checks:
 * - Must be valid URL format
 * - Must use allowed protocol (https/http)
 * - Must be from allowed domain
 * - Must not contain dangerous protocols
 * 
 * @param url - URL to validate
 * @returns true if URL is safe for navigation
 * 
 * @example
 * isValidAppUrl('https://taskify.haat.delivery'); // true
 * isValidAppUrl('javascript:alert(1)'); // false
 * isValidAppUrl('https://malicious.com'); // false
 */
export const isValidAppUrl = (url: string): boolean => {
    // Check for null/undefined/empty
    if (!url || typeof url !== 'string') {
        return false;
    }

    // Check for dangerous protocols first (before URL parsing)
    if (hasDangerousProtocol(url)) {
        return false;
    }

    try {
        const parsed = new URL(url);

        // Check protocol
        if (!ALLOWED_PROTOCOLS.includes(parsed.protocol as typeof ALLOWED_PROTOCOLS[number])) {
            return false;
        }

        // In production, only allow HTTPS
        if (process.env.NODE_ENV === 'production' && parsed.protocol !== 'https:') {
            // Exception for localhost in production (for testing)
            if (!parsed.hostname.includes('localhost') && parsed.hostname !== '127.0.0.1') {
                return false;
            }
        }

        // Check domain
        const isAllowedDomain = ALLOWED_DOMAINS.some(domain => {
            // Exact match or subdomain match
            return parsed.hostname === domain ||
                parsed.hostname.endsWith(`.${domain}`);
        });

        return isAllowedDomain;
    } catch {
        // Invalid URL format
        return false;
    }
};

/**
 * Validates a URL for user-defined external links (My Apps).
 * Allows any host; only enforces http(s) protocol and blocks dangerous protocols.
 * Use for custom app URL and image URL in My Apps.
 *
 * @param url - URL to validate
 * @returns true if URL is safe (http/https, not javascript:, data:, etc.)
 */
export const isValidUserExternalUrl = (url: string): boolean => {
    if (!url || typeof url !== 'string') return false;
    if (hasDangerousProtocol(url)) return false;
    try {
        const parsed = new URL(url.trim());
        return ALLOWED_PROTOCOLS.includes(parsed.protocol as typeof ALLOWED_PROTOCOLS[number]);
    } catch {
        return false;
    }
};

/**
 * Sanitizes a URL for safe display
 * Removes any potentially dangerous content
 * 
 * @param url - URL to sanitize
 * @returns Sanitized URL or empty string if invalid
 */
export const sanitizeUrl = (url: string): string => {
    if (!isValidAppUrl(url)) {
        return '';
    }
    return url.trim();
};

/**
 * Safely navigates to a URL after validation
 * 
 * @param url - URL to navigate to
 * @param fallbackUrl - URL to navigate to if validation fails
 * @returns true if navigation was performed
 */
export const safeNavigate = (url: string, fallbackUrl = '/dashboard'): boolean => {
    if (isValidAppUrl(url)) {
        window.location.href = url;
        return true;
    }

    // Log blocked navigation attempt (development only)
    if (process.env.NODE_ENV === 'development') {
        console.warn(`[Security] Blocked navigation to invalid URL: ${url}`);
    }

    // Navigate to fallback
    window.location.href = fallbackUrl;
    return false;
};

export default {
    isValidAppUrl,
    isValidUserExternalUrl,
    sanitizeUrl,
    safeNavigate,
};

